import { Star, Quote } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const testimonials = [
  {
    name: "Sarah M.",
    role: "Regular Client - 5 Years",
    content: "Lacresha's expertise is unmatched. After years of chronic back pain, her deep tissue work has given me relief I never thought possible. I recommend her to everyone.",
    rating: 5
  },
  {
    name: "Michael T.",
    role: "Local Business Owner",
    content: "As someone who sits at a desk all day, regular massage has become essential for my well-being. Lacresha understands exactly what I need each visit.",
    rating: 5
  },
  {
    name: "Jennifer L.",
    role: "Marathon Runner",
    content: "The sports massage sessions have been game-changing for my training. My recovery time has improved dramatically, and I've avoided injuries I used to struggle with.",
    rating: 5
  },
  {
    name: "David R.",
    role: "Daytona Beach Visitor",
    content: "Found Millennium Wellness Spa during my vacation and it was the highlight of my trip. Professional, relaxing, and truly therapeutic. Will return every visit!",
    rating: 5
  },
  {
    name: "Patricia H.",
    role: "Prenatal Client",
    content: "The prenatal massage sessions helped me so much during my pregnancy. Lacresha was gentle, knowledgeable, and made me feel completely comfortable and cared for.",
    rating: 5
  },
  {
    name: "Robert K.",
    role: "Corporate Client",
    content: "We've brought Lacresha in for corporate wellness days at our office. The team loves it, and we've seen improvements in morale and reduced stress-related absences.",
    rating: 5
  },
  {
    name: "Maria G.",
    role: "Chronic Pain Patient",
    content: "After my doctor recommended massage therapy for my fibromyalgia, I found Lacresha. Her understanding of my condition and careful approach has made a real difference.",
    rating: 5
  },
  {
    name: "James W.",
    role: "Retired Professional",
    content: "At my age, finding someone who understands older bodies is important. Lacresha's hot stone therapy has helped my arthritis tremendously. Highly recommended.",
    rating: 5
  }
];

const stats = [
  { value: "500+", label: "Satisfied Clients" },
  { value: "4.9", label: "Average Rating" },
  { value: "16", label: "Years of Excellence" },
  { value: "95%", label: "Client Retention" },
];

export const TestimonialsPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="testimonials-page">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-[#D4AF37] font-medium tracking-widest uppercase mb-4">
              Client Stories
            </p>
            <h1 className="section-heading text-5xl md:text-6xl font-bold mb-6">
              What Our Clients Say
            </h1>
            <p className="section-subheading">
              Over 16 years, Lacresha Ramos has built a loyal client base through 
              exceptional service and measurable results. Here's what they have to say.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-[#0F172A]" data-testid="testimonial-stats">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={stat.label} className="text-center" data-testid={`testimonial-stat-${index}`}>
                <p className="text-4xl font-bold text-[#D4AF37] mb-1">{stat.value}</p>
                <p className="text-sm text-slate-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-20 bg-white" data-testid="testimonials-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={testimonial.name}
                className="testimonial-card animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`testimonial-card-${index}`}
              >
                <Quote size={32} className="text-[#D4AF37] opacity-30 mb-4" />
                <p className="text-slate-700 leading-relaxed mb-6 relative z-10">
                  "{testimonial.content}"
                </p>
                <div className="flex items-center gap-4 pt-4 border-t border-slate-100">
                  <div className="w-12 h-12 bg-[#0F172A] rounded-full flex items-center justify-center">
                    <span className="text-[#D4AF37] font-semibold">
                      {testimonial.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-[#0F172A]">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.role}</p>
                  </div>
                  <div className="ml-auto flex">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={14} className="text-[#D4AF37] fill-[#D4AF37]" />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Testimonial */}
      <section className="py-20 bg-slate-50" data-testid="featured-testimonial">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <Quote size={48} className="text-[#D4AF37] mx-auto mb-6" />
            <p className="text-2xl md:text-3xl text-[#0F172A] font-display leading-relaxed mb-8">
              "Working with Lacresha has been transformative for both my physical health and 
              mental well-being. Her 16 years of experience show in every session. I've tried 
              many massage therapists, and none compare to the care and expertise at 
              Millennium Wellness Spa."
            </p>
            <div className="flex items-center justify-center gap-4">
              <div className="w-16 h-16 bg-[#0F172A] rounded-full flex items-center justify-center">
                <span className="text-[#D4AF37] font-semibold text-xl">AJ</span>
              </div>
              <div className="text-left">
                <p className="font-semibold text-[#0F172A] text-lg">Amanda J.</p>
                <p className="text-slate-500">Client Since 2019</p>
                <div className="flex mt-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} className="text-[#D4AF37] fill-[#D4AF37]" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6 font-display">
            Ready to Experience the Difference?
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto mb-8">
            Join hundreds of satisfied clients who trust Lacresha Ramos for their 
            wellness needs. Book your first session today.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="btn-secondary h-12 px-8" data-testid="test-contact-btn">
                Book a Session
              </Button>
            </Link>
            <Link to="/services">
              <Button variant="outline" className="h-12 px-8 border-white text-white hover:bg-white/10" data-testid="test-services-btn">
                View Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TestimonialsPage;
